import React from "react";
import { AlertCircle, XCircle, Lock, ServerCrash, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChatErrorType =
  | "assistant_not_found"
  | "assistant_not_public"
  | "server_error"
  | "connection_failed"
  | "unknown";

interface ChatErrorProps {
  type: ChatErrorType;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

const errorConfig: Record<
  ChatErrorType,
  {
    icon: React.ElementType;
    title: string;
    description: string;
    iconColor: string;
    bgColor: string;
  }
> = {
  assistant_not_found: {
    icon: XCircle,
    title: "Assistant Not Found",
    description:
      "The assistant you're looking for doesn't exist or has been removed.",
    iconColor: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
  assistant_not_public: {
    icon: Lock,
    title: "Chat Unavailable",
    description:
      "This assistant is not available for public chat. Please contact the owner for access.",
    iconColor: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
  },
  server_error: {
    icon: ServerCrash,
    title: "Server Error",
    description:
      "We're experiencing technical difficulties. Please try again in a few moments.",
    iconColor: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  connection_failed: {
    icon: WifiOff,
    title: "Connection Failed",
    description:
      "Unable to connect to the chat server. Please check your internet connection and try again.",
    iconColor: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  unknown: {
    icon: AlertCircle,
    title: "Something Went Wrong",
    description: "An unexpected error occurred. Please try again later.",
    iconColor: "text-muted-foreground",
    bgColor: "bg-muted",
  },
};

/**
 * A context-aware error component for the chat widget
 * Displays different messages and icons based on the error type
 */
export function ChatError({
  type,
  message,
  onRetry,
  className,
}: ChatErrorProps) {
  const config = errorConfig[type] || errorConfig.unknown;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center h-full w-full p-8 text-center",
        "animate-in fade-in duration-300",
        className,
      )}
    >
      {/* Icon Container */}
      <div
        className={cn(
          "h-16 w-16 rounded-full flex items-center justify-center mb-4",
          config.bgColor,
        )}
      >
        <Icon className={cn("h-8 w-8", config.iconColor)} />
      </div>

      {/* Title */}
      <h2 className="font-semibold text-lg text-foreground mb-2">
        {config.title}
      </h2>

      {/* Description */}
      <p className="text-muted-foreground text-sm max-w-xs leading-relaxed">
        {message || config.description}
      </p>

      {/* Retry Button */}
      {onRetry && (
        <button
          onClick={onRetry}
          className={cn(
            "mt-6 px-6 py-2.5 rounded-full text-sm font-medium",
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          )}
        >
          Try Again
        </button>
      )}

      {/* Powered by branding */}
      <p className="mt-8 text-xs text-muted-foreground/60">
        Powered by{" "}
        <a
          href="https://smallforcehq.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-muted-foreground transition-colors"
        >
          Smallforce
        </a>
      </p>
    </div>
  );
}
